/**
 * useImmobilien — Datenzugriff für das Immobilien-Modul (Finanzen-Cockpit).
 *
 * Lädt alle immo_*-Tabellen (Inhaber-only, RLS app_is_inhaber()). Folgt dem
 * App-Standard: kein react-query, sondern useState/useEffect + supabase.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface Row { id: string; [k: string]: any; }

/** Geldbetrag im deutschen Format. */
export function fmtEuro(n?: number | null): string {
  if (n === null || n === undefined || isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Datum dd.mm.yyyy. */
export function fmtDatum(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('de-DE');
}

/** Prozent. */
export function fmtProzent(n?: number | null): string {
  if (n === null || n === undefined || isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 }) + ' %';
}

export interface ImmoData {
  objekte: Row[]; einheiten: Row[]; mieter: Row[]; mietvertraege: Row[];
  mieteingaenge: Row[]; kosten: Row[]; darlehen: Row[]; afa: Row[];
  fristen: Row[]; nka: Row[]; dokumente: Row[]; konten: Row[]; steuerjahre: Row[];
  loading: boolean; reload: () => Promise<void>;
}

export function useImmoData(): ImmoData {
  const [objekte, setObjekte] = useState<Row[]>([]);
  const [einheiten, setEinheiten] = useState<Row[]>([]);
  const [mieter, setMieter] = useState<Row[]>([]);
  const [mietvertraege, setMietvertraege] = useState<Row[]>([]);
  const [mieteingaenge, setMieteingaenge] = useState<Row[]>([]);
  const [kosten, setKosten] = useState<Row[]>([]);
  const [darlehen, setDarlehen] = useState<Row[]>([]);
  const [afa, setAfa] = useState<Row[]>([]);
  const [fristen, setFristen] = useState<Row[]>([]);
  const [nka, setNka] = useState<Row[]>([]);
  const [dokumente, setDokumente] = useState<Row[]>([]);
  const [konten, setKonten] = useState<Row[]>([]);
  const [steuerjahre, setSteuerjahre] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    const [o, e, mt, mv, me, k, d, af, fr, nk, dok, ko, st] = await Promise.all([
      supabase.from('immo_objekte').select('*').order('bezeichnung'),
      supabase.from('immo_einheiten').select('*').order('bezeichnung'),
      supabase.from('immo_mieter').select('*').order('name'),
      supabase.from('immo_mietvertraege').select('*'),
      supabase.from('immo_mieteingaenge').select('*'),
      supabase.from('immo_kosten').select('*').order('datum', { ascending: false }),
      supabase.from('immo_darlehen').select('*'),
      supabase.from('immo_afa').select('*'),
      supabase.from('immo_fristen').select('*').order('faellig_am'),
      supabase.from('immo_nebenkostenabrechnung').select('*').order('abrechnungsjahr', { ascending: false }),
      supabase.from('immo_dokumente').select('*').order('datum', { ascending: false }),
      supabase.from('immo_konten').select('*').order('bezeichnung'),
      supabase.from('immo_steuerjahre').select('*').order('jahr', { ascending: false }),
    ]);
    setObjekte(o.data || []);
    setEinheiten(e.data || []);
    setMieter(mt.data || []);
    setMietvertraege(mv.data || []);
    setMieteingaenge(me.data || []);
    setKosten(k.data || []);
    setDarlehen(d.data || []);
    setAfa(af.data || []);
    setFristen(fr.data || []);
    setNka(nk.data || []);
    setDokumente(dok.data || []);
    setKonten(ko.data || []);
    setSteuerjahre(st.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return {
    objekte, einheiten, mieter, mietvertraege, mieteingaenge, kosten,
    darlehen, afa, fristen, nka, dokumente, konten, steuerjahre, loading, reload,
  };
}
